import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, addDoc, serverTimestamp, deleteDoc, setDoc, query, where, orderBy, getDocs } from '../firebase.config';
import { useLanguage } from '../contexts/LanguageContext';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany } from '../utils/dataAdapter';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { formatDateToCompanyFormat } from '../utils/dateUtils';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import { useNotification } from '../contexts/NotificationContext';
import { EmployeeCashManager } from '../utils/employeeCashManager';
import { Wallet, Calendar, Percent, Building2, CreditCard, TrendingDown, TrendingUp, Edit3, Trash2, ChevronDown, ChevronUp, DollarSign, Clock, FileText, PiggyBank, Banknote } from 'lucide-react';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

// Calculate loan interest based on interest rate and time elapsed
// interestFrequency: 'yearly' or 'monthly'
const calculateLoanInterest = (principal, interestRate, loanDate, payments = [], interestFrequency = 'yearly') => {
  if (!principal || !interestRate || !loanDate) return 0;
  
  try {
    const loanStartDate = new Date(loanDate);
    const currentDate = new Date();
    
    // Calculate days elapsed since loan start
    const daysElapsed = Math.max(0, Math.floor((currentDate - loanStartDate) / (1000 * 60 * 60 * 24)));
    
    // Calculate total paid from payments
    const totalPaid = payments.reduce((sum, payment) => {
      const paymentDate = new Date(payment.paymentDate || payment.createdAt?.toDate?.() || currentDate);
      // Only count payments made before or on current date
      if (paymentDate <= currentDate) {
        return sum + (parseFloat(payment.amount) || 0);
      }
      return sum;
    }, 0);
    
    // Calculate remaining principal after payments
    const remainingPrincipal = Math.max(0, principal - totalPaid);
    
    // Calculate interest based on frequency
    const interestRateDecimal = interestRate / 100;
    let interest = 0;
    
    if (interestFrequency === 'monthly') {
      // Monthly interest calculation
      // Calculate months elapsed (more accurate than days/30)
      const monthsElapsed = Math.max(0, (currentDate.getFullYear() - loanStartDate.getFullYear()) * 12 + 
        (currentDate.getMonth() - loanStartDate.getMonth()) + 
        (currentDate.getDate() >= loanStartDate.getDate() ? 0 : -1));
      
      // Monthly rate = annual rate / 12
      const monthlyRate = interestRateDecimal / 12;
      // Interest = Principal × Monthly Rate × Months
      interest = remainingPrincipal * monthlyRate * monthsElapsed;
    } else {
      // Yearly interest calculation (default)
      // Interest = Principal × Rate × Time (in years)
      const timeInYears = daysElapsed / 365;
      interest = remainingPrincipal * interestRateDecimal * timeInYears;
    }
    
    return Math.max(0, interest);
  } catch (error) {
    console.error('Error calculating loan interest:', error);
    return 0;
  }
};

// Loan Card Component
const LoanCard = ({ loan, isExpanded, onToggle, onEdit, onDelete, onPay, companyCurrency, payments = [] }) => {
  // Calculate totalPaid from actual payments (not from outstanding difference)
  // This ensures accuracy even if outstanding was manually edited
  const totalPaidFromPayments = payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
  
  // Calculate interest accrued
  const interestAccrued = calculateLoanInterest(
    loan.principal || 0,
    loan.interestRate || 0,
    loan.loanDate,
    payments,
    loan.interestFrequency || 'yearly' // Default to yearly if not specified
  );
  
  // Calculate outstanding: principal - payments + interest
  const principalOutstanding = Math.max(0, (loan.principal || 0) - totalPaidFromPayments);
  const outstandingWithInterest = principalOutstanding + interestAccrued;
  
  // Use calculated outstanding from payments + interest, or fall back to stored outstanding
  const calculatedOutstanding = payments.length > 0 
    ? outstandingWithInterest
    : (loan.outstanding || loan.principal || 0);
  
  // Use the more accurate value: calculated from payments + interest if available, otherwise stored
  const outstanding = totalPaidFromPayments > 0 ? calculatedOutstanding : (loan.outstanding || loan.principal || 0);
  const totalPaid = totalPaidFromPayments > 0 ? totalPaidFromPayments : ((loan.principal || 0) - (loan.outstanding || loan.principal || 0));
  const progressPercent = loan.principal ? ((totalPaid / loan.principal) * 100).toFixed(1) : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      <div 
        className="cursor-pointer hover:bg-gray-50/50 transition-colors duration-200"
        onClick={() => onToggle(loan.id)}
      >
        {/* Mobile Layout - Enhanced with icons and gradients */}
        <div className="block sm:hidden">
          {/* Gradient header bar */}
          <div className={`h-1.5 ${outstanding <= 0 ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-orange-400 to-red-500'}`}></div>
          
          <div className="p-4">
            {/* Top row with icon, title and expand button */}
            <div className="flex items-start gap-3">
              {/* Loan type icon */}
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                outstanding <= 0 
                  ? 'bg-gradient-to-br from-green-100 to-emerald-100 text-green-600' 
                  : 'bg-gradient-to-br from-orange-100 to-red-100 text-orange-600'
              }`}>
                {loan.loanType?.toLowerCase().includes('home') ? (
                  <Building2 className="w-5 h-5" />
                ) : loan.loanType?.toLowerCase().includes('vehicle') || loan.loanType?.toLowerCase().includes('car') ? (
                  <CreditCard className="w-5 h-5" />
                ) : loan.loanType?.toLowerCase().includes('gold') ? (
                  <PiggyBank className="w-5 h-5" />
                ) : (
                  <Banknote className="w-5 h-5" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-gray-900 truncate">
                    {loan.loanType || 'Loan'}
                  </h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    outstanding <= 0 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {outstanding <= 0 ? (
                      <><TrendingUp className="w-3 h-3" /> Paid</>
                    ) : (
                      <><Clock className="w-3 h-3" /> Active</>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500">
                  <Building2 className="w-3.5 h-3.5" />
                  <span className="truncate">{loan.lender || 'Unknown'}</span>
                  <span className="text-gray-300">•</span>
                  <Percent className="w-3 h-3" />
                  <span>{loan.interestRate || 0}%</span>
                </div>
              </div>
              
              <button
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
            </div>
            
            {/* Progress bar */}
            <div className="mt-3 mb-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-500 font-medium">Repayment Progress</span>
                <span className="text-gray-700 font-semibold">{progressPercent}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    parseFloat(progressPercent) >= 100 
                      ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                      : 'bg-gradient-to-r from-blue-400 to-indigo-500'
                  }`}
                  style={{ width: `${Math.min(100, progressPercent)}%` }}
                ></div>
              </div>
            </div>
            
            {/* Outstanding and Actions row */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-500 font-medium flex items-center gap-1">
                  <TrendingDown className="w-3.5 h-3.5" /> Outstanding
                </p>
                <p className={`text-xl font-bold ${outstanding <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(outstanding, companyCurrency)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {outstanding > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPay(loan);
                    }}
                    className="p-2.5 bg-green-50 text-green-600 hover:bg-green-100 active:bg-green-200 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                    title="Pay Loan"
                  >
                    <Wallet className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(loan);
                  }}
                  className="p-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 active:bg-blue-200 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                  title="Edit"
                >
                  <Edit3 className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(loan.id);
                  }}
                  className="p-2.5 bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:block p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Loan type icon */}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                outstanding <= 0 
                  ? 'bg-gradient-to-br from-green-100 to-emerald-100 text-green-600' 
                  : 'bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600'
              }`}>
                {loan.loanType?.toLowerCase().includes('home') ? (
                  <Building2 className="w-6 h-6" />
                ) : loan.loanType?.toLowerCase().includes('vehicle') || loan.loanType?.toLowerCase().includes('car') ? (
                  <CreditCard className="w-6 h-6" />
                ) : loan.loanType?.toLowerCase().includes('gold') ? (
                  <PiggyBank className="w-6 h-6" />
                ) : (
                  <Banknote className="w-6 h-6" />
                )}
              </div>
              
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-gray-900 truncate">
                    {loan.loanType || 'Loan'}
                  </h3>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    outstanding <= 0 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {outstanding <= 0 ? (
                      <><TrendingUp className="w-3 h-3" /> Paid Off</>
                    ) : (
                      <><Clock className="w-3 h-3" /> Active</>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                  <span className="flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    {loan.lender || 'Unknown Lender'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Percent className="w-4 h-4 text-gray-400" />
                    {loan.interestRate || 0}% p.a. ({loan.interestFrequency === 'monthly' ? 'monthly' : 'yearly'})
                  </span>
                  {loan.emi && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      EMI: {formatCurrency(loan.emi, companyCurrency)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">Outstanding</p>
                <p className={`text-xl font-bold ${outstanding <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(outstanding, companyCurrency)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {outstanding > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPay(loan);
                    }}
                    className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                    title="Pay Loan"
                  >
                    <Wallet className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(loan);
                  }}
                  className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Edit"
                >
                  <Edit3 className="w-5 h-5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(loan.id);
                  }}
                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Loan Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Loan Type:</span>
                  <span className="font-medium">{loan.loanType || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Lender:</span>
                  <span className="font-medium">{loan.lender || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Loan Date:</span>
                  <span className="font-medium">{formatDateToCompanyFormat(loan.loanDate, {})}</span>
                </div>
                {loan.tenure && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tenure:</span>
                    <span className="font-medium">{loan.tenure} months</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Interest Rate:</span>
                  <span className="font-medium">{loan.interestRate || 0}% p.a. ({loan.interestFrequency === 'monthly' ? 'Monthly' : 'Yearly'} calculation)</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Financial Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Principal:</span>
                  <span className="font-medium">{formatCurrency(loan.principal || 0, companyCurrency)}</span>
                </div>
                {interestAccrued > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Interest Accrued:</span>
                    <span className="font-medium text-orange-600">{formatCurrency(interestAccrued, companyCurrency)}</span>
                  </div>
                )}
                {loan.emi && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">EMI:</span>
                    <span className="font-medium">{formatCurrency(loan.emi, companyCurrency)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Paid:</span>
                  <span className="font-medium text-green-600">{formatCurrency(totalPaid, companyCurrency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Outstanding:</span>
                  <span className="font-bold text-red-600 text-lg">{formatCurrency(outstanding, companyCurrency)}</span>
                </div>
                <div className="mt-2">
                  <div className="flex justify-between mb-1">
                    <span className="text-xs text-gray-500">Repayment Progress:</span>
                    <span className="text-xs font-medium">{progressPercent}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{ width: `${progressPercent}%` }}
                    ></div>
                  </div>
                </div>
                {loan.description && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Description:</span>
                    <span className="font-medium">{loan.description}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Payment History */}
          {payments && payments.length > 0 && (
            <div className="mt-4 border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Payment History</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex justify-between items-center p-2 bg-white rounded border border-gray-200 text-sm">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{formatCurrency(payment.amount || 0, companyCurrency)}</div>
                      <div className="text-xs text-gray-500">
                        {formatDateToCompanyFormat(payment.paymentDate, {})} • {payment.paymentMode || 'cash'}
                        {payment.reference && ` • Ref: ${payment.reference}`}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {payment.notes && (
                        <div className="text-gray-400 italic">{payment.notes}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function Loans({ db, userId, isAuthReady, appId }) {
  const { currentCompany, employees } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  const { currency: companyCurrency } = useCompanyCurrency();
  const { t } = useLanguage();
  const { success, error, warning } = useNotification();

  const [loans, setLoans] = useState([]);
  const [expandedLoans, setExpandedLoans] = useState({});
  const [editingLoanId, setEditingLoanId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingLoanId, setDeletingLoanId] = useState(null);
  
  // Loan payment states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedLoanForPayment, setSelectedLoanForPayment] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [recordInEmployeeCashFlow, setRecordInEmployeeCashFlow] = useState(false);
  const [selectedEmployeeForCashFlow, setSelectedEmployeeForCashFlow] = useState('');
  const [loanPayments, setLoanPayments] = useState({}); // { loanId: [payments] }

  // Form state
  const [loanType, setLoanType] = useState('Business Loan');
  const [lender, setLender] = useState('');
  const [loanDate, setLoanDate] = useState(new Date().toISOString().split('T')[0]);
  const [principal, setPrincipal] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [interestFrequency, setInterestFrequency] = useState('yearly'); // 'yearly' or 'monthly'
  const [tenure, setTenure] = useState('');
  const [emi, setEmi] = useState('');
  const [outstanding, setOutstanding] = useState('');
  const [description, setDescription] = useState('');

  // Filter states
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState('');

  const loanTypes = ['Business Loan', 'Personal Loan', 'Home Loan', 'Vehicle Loan', 'Gold Loan', 'Other'];

  // Calculate EMI if not provided
  const calculateEMI = (principal, interestRate, tenure) => {
    if (!principal || !interestRate || !tenure) return 0;
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = tenure;
    if (monthlyRate === 0) return principal / numPayments;
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
           (Math.pow(1 + monthlyRate, numPayments) - 1);
  };

  // Fetch loans
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const loansCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'loans');
      if (!loansCollectionRef) {
        setLoans([]);
        return;
      }
      
      const unsubscribe = onSnapshot(loansCollectionRef, (snapshot) => {
        const arr = [];
        snapshot.forEach((doc) => {
          arr.push({ id: doc.id, ...doc.data() });
        });
        setLoans(arr);
      }, (err) => {
        console.error('Error fetching loans:', err);
        setLoans([]);
      });
      return () => {
        unsubscribe();
        setLoans([]);
      };
    } else {
      setLoans([]);
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch loan payments
  useEffect(() => {
    if (db && userId && isAuthReady && companyId && loans.length > 0) {
      const fetchLoanPayments = async () => {
        try {
          const loanPaymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'loanPayments');
          const paymentsQuery = query(loanPaymentsRef, orderBy('paymentDate', 'desc'));
          const paymentsSnap = await getDocs(paymentsQuery);
          
          const paymentsByLoan = {};
          paymentsSnap.forEach((doc) => {
            const payment = { id: doc.id, ...doc.data() };
            const loanId = payment.loanId;
            if (!paymentsByLoan[loanId]) {
              paymentsByLoan[loanId] = [];
            }
            paymentsByLoan[loanId].push(payment);
          });
          setLoanPayments(paymentsByLoan);
        } catch (err) {
          console.error('Error fetching loan payments:', err);
        }
      };
      
      fetchLoanPayments();
    }
  }, [db, userId, isAuthReady, appId, companyId, loans]);

  // Toggle expand
  const toggleExpand = (loanId) => {
    setExpandedLoans(prev => ({
      ...prev,
      [loanId]: !prev[loanId]
    }));
  };

  // Handle edit
  const handleEdit = (loan) => {
    setEditingLoanId(loan.id);
    setLoanType(loan.loanType || 'Business Loan');
    setLender(loan.lender || '');
    setLoanDate(loan.loanDate || new Date().toISOString().split('T')[0]);
    setPrincipal(loan.principal?.toString() || '');
    setInterestRate(loan.interestRate?.toString() || '');
    setInterestFrequency(loan.interestFrequency || 'yearly');
    setTenure(loan.tenure?.toString() || '');
    setEmi(loan.emi?.toString() || '');
    setOutstanding(loan.outstanding?.toString() || loan.principal?.toString() || '');
    setDescription(loan.description || '');
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = (loanId) => {
    setDeletingLoanId(loanId);
    setShowDeleteConfirm(true);
  };

  // Handle pay loan
  const handlePayLoan = (loan) => {
    setSelectedLoanForPayment(loan);
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMode('cash');
    setPaymentReference('');
    setPaymentNotes('');
    setRecordInEmployeeCashFlow(false);
    setSelectedEmployeeForCashFlow('');
    setShowPaymentModal(true);
  };

  // Handle save loan payment
  const handleSaveLoanPayment = async () => {
    if (!selectedLoanForPayment || !paymentAmount) {
      warning('Please enter payment amount');
      return;
    }

    const paymentAmountNum = parseFloat(paymentAmount);
    if (isNaN(paymentAmountNum) || paymentAmountNum <= 0) {
      warning('Please enter a valid payment amount');
      return;
    }

    const outstanding = selectedLoanForPayment.outstanding || 0;
    if (paymentAmountNum > outstanding) {
      warning(`Payment amount cannot exceed outstanding amount of ${formatCurrency(outstanding, companyCurrency)}`);
      return;
    }

    if (recordInEmployeeCashFlow && !selectedEmployeeForCashFlow) {
      warning('Please select an employee for cash flow recording');
      return;
    }

    try {
      const companyData = {
        companyId: currentCompany?.companyId,
        userId: currentCompany?.userId,
        appId: currentCompany?.appId
      };

      // Validate employee balance if using employee wallet
      if (recordInEmployeeCashFlow && selectedEmployeeForCashFlow) {
        const selectedEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
        if (selectedEmployee) {
          if ((selectedEmployee.status || 'active') !== 'active') {
            error('Selected employee is inactive. Please choose an active employee.', {
              title: 'Inactive Employee',
              duration: 5000
            });
            return;
          }
          const mappedPaymentMode = paymentMode === 'upi' ? 'upi' : 'cash';
          const employeeBalance = await EmployeeCashManager.getEmployeeBalance(companyData, selectedEmployeeForCashFlow);
          const availableBalance = mappedPaymentMode === 'cash' ? employeeBalance.cash : mappedPaymentMode === 'upi' ? employeeBalance.upi : employeeBalance.total;
          
          if (availableBalance < paymentAmountNum) {
            error(`Insufficient balance! ${selectedEmployee.name} has ${formatCurrency(availableBalance, companyCurrency)} ${mappedPaymentMode === 'cash' ? 'cash' : 'UPI'} balance, but needs ${formatCurrency(paymentAmountNum, companyCurrency)}`, {
              title: 'Insufficient Balance',
              duration: 6000
            });
            return;
          }
        }
      }

      // Create loan payment record
      const loanPaymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'loanPayments');
      const paymentData = {
        loanId: selectedLoanForPayment.id,
        loanType: selectedLoanForPayment.loanType,
        lender: selectedLoanForPayment.lender,
        amount: paymentAmountNum,
        paymentDate: paymentDate,
        paymentMode: paymentMode,
        reference: paymentReference.trim(),
        notes: paymentNotes.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const paymentDocRef = await addDoc(loanPaymentsRef, paymentData);

      // Update loan outstanding amount
      const loansCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'loans');
      const loanRef = doc(loansCollectionRef, selectedLoanForPayment.id);
      const newOutstanding = Math.max(0, outstanding - paymentAmountNum);
      
      await setDoc(loanRef, {
        outstanding: newOutstanding,
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Record in employee cash flow if enabled
      if (recordInEmployeeCashFlow && selectedEmployeeForCashFlow) {
        try {
          const selectedEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
          if (selectedEmployee) {
            if ((selectedEmployee.status || 'active') !== 'active') {
              error('Selected employee is inactive. Please choose an active employee.', {
                title: 'Inactive Employee',
                duration: 5000
              });
              return;
            }
            const mappedPaymentMode = paymentMode === 'upi' ? 'upi' : 'cash';
            
            const employeeTransactionData = {
              employeeId: selectedEmployeeForCashFlow,
              employeeName: selectedEmployee.name || '',
              transactionType: 'outflow', // Loan payment is outflow from employee
              amount: paymentAmountNum,
              paymentMode: mappedPaymentMode,
              description: `Loan Payment - ${selectedLoanForPayment.loanType} (${selectedLoanForPayment.lender})`,
              referenceNumber: paymentReference.trim() || paymentDocRef.id,
              date: paymentDate,
              category: 'Loan Repayment',
              createdBy: {
                userId: userId || currentCompany?.userId || '',
                name: currentCompany?.userName || 'User'
              },
              linkedLoanId: selectedLoanForPayment.id, // Link to loan
              linkedLoanPaymentId: paymentDocRef.id, // Link to loan payment record
              ...(mappedPaymentMode === 'cash' && {
                cashGivenTo: selectedLoanForPayment.lender || 'Lender'
              }),
              ...(mappedPaymentMode === 'upi' && {
                upiId: selectedEmployee.paymentPreferences?.upiId || '',
                transactionId: paymentReference.trim() || '',
                upiApp: selectedEmployee.paymentPreferences?.upiApp || 'Other'
              })
            };
            
            const employeeResult = await EmployeeCashManager.createCashTransaction(
              companyData, 
              employeeTransactionData
            );
            
            if (employeeResult.success) {
              success('Loan payment recorded and deducted from employee balance', {
                title: 'Payment Successful',
                duration: 4000
              });
            } else {
              // Payment was recorded but employee transaction failed - update payment record
              await setDoc(paymentDocRef, {
                employeeTransactionError: employeeResult.error,
                updatedAt: serverTimestamp()
              }, { merge: true });
              
              warning(`Loan payment recorded but failed to update employee balance: ${employeeResult.error}`, {
                title: 'Partial Success',
                duration: 6000
              });
            }
          }
        } catch (employeeError) {
          console.error('Error recording in employee cash flow:', employeeError);
          // Update payment record with error
          await setDoc(paymentDocRef, {
            employeeTransactionError: employeeError.message,
            updatedAt: serverTimestamp()
          }, { merge: true });
          
          warning(`Loan payment recorded but failed to update employee balance: ${employeeError.message}`, {
            title: 'Partial Success',
            duration: 6000
          });
        }
      } else {
        success('Loan payment recorded successfully', {
          title: 'Payment Successful',
          duration: 3000
        });
      }

      // Reset form and close modal
      setShowPaymentModal(false);
      setSelectedLoanForPayment(null);
      setPaymentAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMode('cash');
      setPaymentReference('');
      setPaymentNotes('');
      setRecordInEmployeeCashFlow(false);
      setSelectedEmployeeForCashFlow('');
    } catch (err) {
      console.error('Error saving loan payment:', err);
      error('Error saving loan payment: ' + err.message, {
        title: 'Payment Failed',
        duration: 6000
      });
    }
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!db || !userId || !appId || !deletingLoanId) return;
    try {
      const loanRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, 'loans'), deletingLoanId);
      await deleteDoc(loanRef);
      success('Loan deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingLoanId(null);
    } catch (err) {
      error('Error deleting loan: ' + err.message);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!db || !userId || !appId) return;
    if (!lender.trim()) {
      warning('Please enter lender name');
      return;
    }
    if (!principal || parseFloat(principal) <= 0) {
      warning('Please enter valid principal amount');
      return;
    }
    if (!outstanding || parseFloat(outstanding) < 0) {
      warning('Please enter valid outstanding amount');
      return;
    }

    try {
      const loansCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'loans');
      const principalAmount = parseFloat(principal);
      const outstandingAmount = parseFloat(outstanding);
      const calculatedEMI = emi ? parseFloat(emi) : 
        (interestRate && tenure ? calculateEMI(principalAmount, parseFloat(interestRate), parseInt(tenure)) : 0);
      
      const loanData = {
        loanType,
        lender: lender.trim(),
        loanDate,
        principal: principalAmount,
        interestRate: interestRate ? parseFloat(interestRate) : 0,
        interestFrequency: interestFrequency || 'yearly', // Store interest calculation frequency
        tenure: tenure ? parseInt(tenure) : null,
        emi: calculatedEMI,
        outstanding: outstandingAmount,
        description: description.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (editingLoanId) {
        const loanRef = doc(loansCollectionRef, editingLoanId);
        await setDoc(loanRef, loanData, { merge: true });
        success('Loan updated successfully');
      } else {
        await addDoc(loansCollectionRef, loanData);
        success('Loan added successfully');
      }

      // Reset form
      setLoanType('Business Loan');
      setLender('');
      setLoanDate(new Date().toISOString().split('T')[0]);
      setPrincipal('');
      setInterestRate('');
      setInterestFrequency('yearly');
      setTenure('');
      setEmi('');
      setOutstanding('');
      setDescription('');
      setEditingLoanId(null);
      setShowForm(false);
    } catch (err) {
      error('Error saving loan: ' + err.message);
    }
  };

  // Filter loans
  const filteredLoans = (loans || []).filter(loan => {
    const matchesSearch = !filterSearch || 
      (loan.loanType || '').toLowerCase().includes(filterSearch.toLowerCase()) ||
      (loan.lender || '').toLowerCase().includes(filterSearch.toLowerCase());
    const matchesType = !filterType || loan.loanType === filterType;
    return matchesSearch && matchesType;
  });

  // Table pagination
  const pagination = useTablePagination(filteredLoans || [], 10);

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Loans</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingLoanId(null);
            setLoanType('Business Loan');
            setLender('');
            setLoanDate(new Date().toISOString().split('T')[0]);
            setPrincipal('');
            setInterestRate('');
            setInterestFrequency('yearly');
            setTenure('');
            setEmi('');
            setOutstanding('');
            setDescription('');
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md"
        >
          + Add Loan
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Search loans..."
            />
          </div>
          <div className="sm:w-48">
            <AppSelect
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              aria-label="Loan type filter"
              sheetTitle="Loan type"
              options={[
                { value: '', label: 'All Types' },
                ...loanTypes.map(type => ({ value: type, label: type })),
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>
        </div>
      </div>

      {/* Loans List */}
      <div className="space-y-4">
        {(pagination.currentData || []).map(loan => (
          <LoanCard
            key={loan.id}
            loan={loan}
            isExpanded={expandedLoans[loan.id]}
            onToggle={toggleExpand}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onPay={handlePayLoan}
            companyCurrency={companyCurrency}
            payments={loanPayments[loan.id] || []}
          />
        ))}
        {(!pagination.currentData || pagination.currentData.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No loans found</p>
            <p className="text-sm mt-2">Add your first loan to get started</p>
          </div>
        )}
      </div>

      <PaginationControls {...pagination} />

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">
                {editingLoanId ? 'Edit Loan' : 'Add Loan'}
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Loan Type *
                    </label>
                    <AppSelect
                      value={loanType}
                      onChange={(e) => setLoanType(e.target.value)}
                      aria-label="Loan type"
                      sheetTitle="Loan type"
                      options={loanTypes.map(type => ({ value: type, label: type }))}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} py-2`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Lender *
                    </label>
                    <input
                      type="text"
                      value={lender}
                      onChange={(e) => setLender(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="e.g., SBI, HDFC Bank"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Loan Date *
                    </label>
                    <input
                      type="date"
                      value={loanDate}
                      onChange={(e) => setLoanDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Principal Amount *
                    </label>
                    <input
                      type="number"
                      value={principal}
                      onChange={(e) => {
                        setPrincipal(e.target.value);
                        if (!outstanding) setOutstanding(e.target.value);
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interest Rate (% p.a.)
                    </label>
                    <input
                      type="number"
                      value={interestRate}
                      onChange={(e) => {
                        setInterestRate(e.target.value);
                        if (principal && e.target.value && tenure) {
                          const calculated = calculateEMI(parseFloat(principal), parseFloat(e.target.value), parseInt(tenure));
                          setEmi(calculated.toFixed(2));
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="0.00"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interest Calculation
                    </label>
                    <AppSelect
                      value={interestFrequency}
                      onChange={(e) => setInterestFrequency(e.target.value)}
                      aria-label="Interest calculation"
                      sheetTitle="Interest calculation"
                      options={[
                        { value: 'yearly', label: 'Yearly (per annum)' },
                        { value: 'monthly', label: 'Monthly' },
                      ]}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} py-2`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {interestFrequency === 'yearly' 
                        ? 'Interest calculated annually' 
                        : 'Interest calculated monthly (rate/12 per month)'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tenure (months)
                    </label>
                    <input
                      type="number"
                      value={tenure}
                      onChange={(e) => {
                        setTenure(e.target.value);
                        if (principal && interestRate && e.target.value) {
                          const calculated = calculateEMI(parseFloat(principal), parseFloat(interestRate), parseInt(e.target.value));
                          setEmi(calculated.toFixed(2));
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="0"
                      min="1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      EMI
                    </label>
                    <input
                      type="number"
                      value={emi}
                      onChange={(e) => setEmi(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Auto-calculated"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Outstanding Amount *
                  </label>
                  <input
                    type="number"
                    value={outstanding}
                    onChange={(e) => setOutstanding(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder={principal || '0.00'}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                    placeholder="Additional details about the loan"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSave}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
                >
                  {editingLoanId ? 'Update' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingLoanId(null);
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-lg font-bold mb-4">Confirm Delete</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this loan? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingLoanId(null);
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loan Payment Modal */}
      {showPaymentModal && selectedLoanForPayment && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Pay Loan</h2>
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Loan Type:</span>
                    <span className="font-medium ml-2">{selectedLoanForPayment.loanType || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Lender:</span>
                    <span className="font-medium ml-2">{selectedLoanForPayment.lender || '-'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Principal:</span>
                    <span className="font-medium ml-2">{formatCurrency(selectedLoanForPayment.principal || 0, companyCurrency)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Outstanding:</span>
                    <span className="font-bold text-red-600 ml-2">{formatCurrency(selectedLoanForPayment.outstanding || 0, companyCurrency)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Amount *
                    </label>
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder={`Max: ${formatCurrency(selectedLoanForPayment.outstanding || 0, companyCurrency)}`}
                      min="0"
                      max={selectedLoanForPayment.outstanding || 0}
                      step="0.01"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Outstanding: {formatCurrency(selectedLoanForPayment.outstanding || 0, companyCurrency)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Date *
                    </label>
                    <input
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Mode *
                    </label>
                    <AppSelect
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      aria-label="Payment mode"
                      sheetTitle="Payment mode"
                      options={[
                        { value: 'cash', label: 'Cash' },
                        { value: 'upi', label: 'UPI' },
                        { value: 'bank', label: 'Bank Transfer' },
                        { value: 'cheque', label: 'Cheque' },
                      ]}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} py-2`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Transaction ID / Cheque Number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    rows="3"
                    placeholder="Additional notes about this payment"
                  />
                </div>

                {/* Employee Cash Flow Integration */}
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      id="recordInEmployeeCashFlow"
                      checked={recordInEmployeeCashFlow}
                      onChange={(e) => {
                        setRecordInEmployeeCashFlow(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedEmployeeForCashFlow('');
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="recordInEmployeeCashFlow" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Pay from Employee Wallet
                    </label>
                  </div>
                  
                  {recordInEmployeeCashFlow && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Employee *
                      </label>
                      <AppSelect
                        value={selectedEmployeeForCashFlow}
                        onChange={(e) => setSelectedEmployeeForCashFlow(e.target.value)}
                        placeholder="Select Employee"
                        aria-label="Select employee"
                        sheetTitle="Select employee"
                        options={[
                          { value: '', label: 'Select Employee' },
                          ...(employees?.filter(emp => emp.status === 'active').map((employee) => {
                            const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                            const availableBalance = paymentMode === 'cash' ? balance.cash : paymentMode === 'upi' ? balance.upi : balance.total;
                            return {
                              value: employee.id,
                              label: `${employee.name} (${employee.employeeId || 'N/A'}) - ${formatCurrency(availableBalance, companyCurrency)} ${paymentMode === 'cash' ? 'Cash' : paymentMode === 'upi' ? 'UPI' : 'Total'} available`,
                            };
                          }) || []),
                        ]}
                        triggerClassName={APP_SELECT_FILTER_TRIGGER}
                      />
                      
                      {selectedEmployeeForCashFlow && (() => {
                        const selectedEmp = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
                        const balance = selectedEmp?.cashBalance || { cash: 0, upi: 0, total: 0 };
                        const availableBalance = paymentMode === 'cash' ? balance.cash : paymentMode === 'upi' ? balance.upi : balance.total;
                        const paymentAmountNum = parseFloat(paymentAmount) || 0;
                        const isSufficient = availableBalance >= paymentAmountNum;
                        
                        return (
                          <div className="mt-3 text-xs">
                            <div className="font-medium text-gray-700 mb-1">
                              Selected: {selectedEmp?.name} ({selectedEmp?.employeeId || 'N/A'})
                            </div>
                            <div className="text-gray-600">
                              <div>Available {paymentMode === 'cash' ? 'Cash' : paymentMode === 'upi' ? 'UPI' : 'Total'}: {formatCurrency(availableBalance, companyCurrency)}</div>
                              <div className={`mt-1 ${isSufficient ? 'text-green-600' : 'text-red-600'}`}>
                                {isSufficient 
                                  ? `✓ Sufficient balance` 
                                  : `✗ Insufficient balance (Need ${formatCurrency(paymentAmountNum - availableBalance, companyCurrency)} more)`}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSaveLoanPayment}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md"
                >
                  Pay Loan
                </button>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedLoanForPayment(null);
                    setPaymentAmount('');
                    setPaymentDate(new Date().toISOString().split('T')[0]);
                    setPaymentMode('cash');
                    setPaymentReference('');
                    setPaymentNotes('');
                    setRecordInEmployeeCashFlow(false);
                    setSelectedEmployeeForCashFlow('');
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Loans;

